import "./WorldSection.css";
import worldSvg from "/assets/World.svg";
import imageOcclusion from "/assets/image-occlusion.png";
import DarkCard from "./DarkCard";
import Tag from "./Tag";

import arts from "/assets/arts.png";
import biology from "/assets/biology.png";
import business from "/assets/business.png";
import chemistry from "/assets/chemistry.png";
import engineering from "/assets/engineering.png";
import history from "/assets/history.png";
import languages from "/assets/languages.png";
import law from "/assets/law.png";
import math from "/assets/math.png";
import { useEffect, useState } from "react";
import {
  AtomIcon,
  HourglassIcon,
  BriefcaseBusinessIcon,
  ScaleIcon,
  BrushIcon,
  LanguagesIcon,
  SquareFunctionIcon,
  FolderCodeIcon,
  BriefcaseMedicalIcon,
  FlaskConicalIcon,
  MousePointerClickIcon,
} from "lucide-react";

const features = {
  medicine: {
    tag: "Medicine",
    icon: <BriefcaseMedicalIcon className="w-icon-md h-icon-md flex-none" />,
    title: "Image Occlusion Flashcards",
    description:
      "Master complex medical diagrams effortlessly. With Image Occlusion flashcards, you can hide and reveal key parts of anatomical structures, medical charts, and diagrams to test your knowledge in an interactive way.",
    image: imageOcclusion,
  },
  math: {
    tag: "Math",
    icon: <SquareFunctionIcon className="w-icon-md h-icon-md flex-none" />,
    title: "Stories Mnemonics",
    description:
      "Turn abstract math concepts into memorable stories. Our AI-powered story mnemonics help you retain formulas, equations, and problem-solving techniques by linking them to engaging narratives.",
    image: math,
  },
  languages: {
    tag: "Languages",
    icon: <LanguagesIcon className="w-icon-md h-icon-md flex-none" />,
    title: "Type to Answer",
    description:
      "Improve your language skills by actively recalling words and phrases. The 'Type to Answer' feature forces you to engage with new vocabulary and grammar rules through typing, reinforcing learning and retention.",
    image: languages,
  },
  law: {
    tag: "Law",
    icon: <ScaleIcon className="w-icon-md h-icon-md flex-none" />,
    title: "Examples & Case Studies",
    description:
      "Master legal concepts with real-world examples and case studies. Our system provides practical applications of laws and precedents, helping you understand how legal principles work in different scenarios.",
    image: law,
  },
  chemestry: {
    tag: "Chemistry",
    icon: <AtomIcon className="w-icon-md h-icon-md flex-none" />,
    title: "Auto-Generated Quizzes",
    description:
      "Test your chemistry knowledge with AI-generated quizzes. Practice balancing chemical equations, understanding reactions, and memorizing the periodic table through personalized assessments.",
    image: chemistry,
  },
  biology: {
    tag: "Biology",
    icon: <FlaskConicalIcon className="w-icon-md h-icon-md flex-none" />,
    title: "Spaced Repetition Algorithm",
    description:
      "Retain biological concepts more effectively with Spaced Repetition. Our AI schedules reviews of key topics—like cell structures, genetics, and ecology—at optimal intervals to reinforce long-term memory.",
    image: biology,
  },
  engineering: {
    tag: "Engineering",
    icon: <FolderCodeIcon className="w-icon-md h-icon-md flex-none" />,
    title: "Explanation & Examples",
    description:
      "Break down complex engineering principles with detailed explanations and real-world examples. Learn the mechanics, physics, and problem-solving techniques that engineers use every day.",
    image: engineering,
  },
  arts: {
    tag: "Arts",
    icon: <BrushIcon className="w-icon-md h-icon-md flex-none" />,
    title: "Visualize Every Masterpiece",
    description:
      "Study art in a whole new way. Professor Ka can help you visualise every piece of art and provide history, and techniques behind the world's greatest artworks, helping you develop a deeper appreciation and understanding.",
    image: arts,
  },
  business: {
    tag: "Business",
    icon: <BriefcaseBusinessIcon className="w-icon-md h-icon-md flex-none" />,
    title: "Bulk Generate Thousands of Flashcards",
    description:
      "Speed up your business studies with AI-powered flashcard generation. Instantly create hundreds of flashcards covering key concepts like marketing strategies, financial principles, and economic theories to optimize your learning efficiency.",
    image: business,
  },
  history: {
    tag: "History",
    icon: <HourglassIcon className="w-icon-md h-icon-md flex-none" />,
    title: "Cloze Deletion Cards",
    description:
      "Enhance your memory of historical events with Cloze Deletion cards. By filling in missing dates, names, and facts, you actively engage with the material, reinforcing key historical knowledge.",
    image: history,
  },
};

type FeatureKey = keyof typeof features;

const WorldSection = () => {
  const [activeTag, setActiveTag] = useState<FeatureKey>("medicine");
  const [autoSwitch, setAutoSwitch] = useState(true);

  useEffect(() => {
    if (!autoSwitch) return; // Stop auto-switching if user clicks a tag

    const tagKeys = Object.keys(features) as Array<FeatureKey>;
    let index = tagKeys.indexOf(activeTag);

    const interval = setInterval(() => {
      index = (index + 1) % tagKeys.length; // Loop through tags
      setActiveTag(tagKeys[index]);
    }, 2000);

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [activeTag, autoSwitch]);

  const handleTagClick = (key: FeatureKey) => {
    setActiveTag(key);
    setAutoSwitch(false); // Stop auto-switching
  };

  return (
    <div className="world">
      <div className="invert-div">
        <div className="world-icons">
          <img
            className="worldSvg"
            src={worldSvg}
            alt="A wireframed world with different tags representing subjects"
          />
          {Object.entries(features).map(([key, feature]) => (
            <div
              key={key}
              className="clickable-tag"
              data-tag={key}
              onClick={() => handleTagClick(key as FeatureKey)}
            >
              <Tag
                size="m"
                variant={activeTag === key ? "active-color" : "default-color"}
              >
                {feature.icon}
                {feature.tag}
              </Tag>
            </div>
          ))}
        </div>
        <h2 style={{ color: "#E5E5E5" }}>
          Whatever you study <br /> we have you covered.
        </h2>
      </div>
      <p>
        <MousePointerClickIcon className="w-icon-sm h-icon-sm text-body-600 flex-none" />
        Click on your subject to see what we’ve made for you
      </p>

      {/* Dark Card with Icon Inside */}
      <DarkCard
        tag={features[activeTag].tag}
        icon={features[activeTag].icon}
        title={features[activeTag].title}
        description={features[activeTag].description}
        image={features[activeTag].image}
      />
    </div>
  );
};

export default WorldSection;
